import type { ReactNode } from 'react'
import type { LayoutChangeEvent, ViewStyle } from 'react-native'
import { useEffect, useId, useMemo, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedProps,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg'
import { colors } from '@/src/theme'

import { VOICE_ASSESSMENT_CARD_INNER_PADDING_STYLE } from '../../constants/statistics.styles'

const AnimatedRect = Animated.createAnimatedComponent(Rect)

const BORDER_WIDTH = 2
const OUTER_BORDER_RADIUS = 8
const INNER_BORDER_RADIUS = OUTER_BORDER_RADIUS - BORDER_WIDTH
/** One full travel around the border; tweak for perceived speed. */
const BORDER_CHASE_DURATION_MS = 2200
/** Visible arc length as a fraction of perimeter (rest is gap). */
const DASH_VISIBLE_RATIO = 0.38

function roundedRectPerimeter(
  rectWidth: number,
  rectHeight: number,
  rx: number,
): number {
  if (rectWidth <= 0 || rectHeight <= 0) {
    return 0
  }
  const r = Math.min(rx, rectWidth / 2, rectHeight / 2)
  return 2 * (rectWidth + rectHeight - 2 * r) + 2 * Math.PI * r
}

interface VoiceAssessmentAnalyzingBorderShellProps {
  children: ReactNode
  style?: ViewStyle
}

export function VoiceAssessmentAnalyzingBorderShell({
  children,
  style,
}: VoiceAssessmentAnalyzingBorderShellProps) {
  const reduceMotion = useReducedMotion()
  const reactId = useId()
  const gradId = useMemo(
    () =>
      `va-analyzing-border-${reactId.replace(/[^a-zA-Z0-9_-]/g, '') || 'grad'}`,
    [reactId],
  )

  const strokeDashoffset = useSharedValue(0)
  const [layout, setLayout] = useState({ width: 0, height: 0 })

  const { perimeter, rectX, rectY, rectW, rectH, rxStroke } = useMemo(() => {
    const { width: outerW, height: outerH } = layout
    if (outerW <= 0 || outerH <= 0) {
      return {
        perimeter: 0,
        rectX: 0,
        rectY: 0,
        rectW: 0,
        rectH: 0,
        rxStroke: OUTER_BORDER_RADIUS,
      }
    }
    const rw = outerW - BORDER_WIDTH
    const rh = outerH - BORDER_WIDTH
    const rx = Math.min(OUTER_BORDER_RADIUS, rw / 2, rh / 2)
    return {
      perimeter: roundedRectPerimeter(rw, rh, rx),
      rectX: BORDER_WIDTH / 2,
      rectY: BORDER_WIDTH / 2,
      rectW: rw,
      rectH: rh,
      rxStroke: rx,
    }
  }, [layout])

  const dashVisible =
    perimeter > 0 ? Math.max(12, perimeter * DASH_VISIBLE_RATIO) : 0
  const dashGap = perimeter > 0 ? Math.max(0, perimeter - dashVisible) : 0

  useEffect(() => {
    cancelAnimation(strokeDashoffset)
    if (reduceMotion || perimeter <= 0) {
      strokeDashoffset.value = 0
      return
    }
    strokeDashoffset.value = 0
    strokeDashoffset.value = withRepeat(
      withTiming(perimeter, {
        duration: BORDER_CHASE_DURATION_MS,
        easing: Easing.linear,
      }),
      -1,
      false,
    )
    return () => {
      cancelAnimation(strokeDashoffset)
    }
  }, [perimeter, reduceMotion, strokeDashoffset])

  const animatedStrokeProps = useAnimatedProps(() => ({
    strokeDashoffset: strokeDashoffset.value,
  }))

  const onShellLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout
    setLayout((previous) =>
      previous.width === width && previous.height === height
        ? previous
        : { width, height },
    )
  }

  const showSvg = layout.width > 0 && layout.height > 0 && perimeter > 0

  return (
    <View
      className="overflow-hidden"
      style={[
        {
          borderRadius: OUTER_BORDER_RADIUS,
        },
        style,
      ]}
      onLayout={onShellLayout}
    >
      {showSvg ? (
        <Svg
          width={layout.width}
          height={layout.height}
          style={[StyleSheet.absoluteFill, { pointerEvents: 'none' }]}
        >
          <Defs>
            <LinearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor="#FFE8F2" />
              <Stop offset="35%" stopColor="#F8A8CF" />
              <Stop offset="65%" stopColor={colors.corePink} />
              <Stop offset="100%" stopColor="#9E0850" />
            </LinearGradient>
          </Defs>
          {reduceMotion ? (
            <Rect
              x={rectX}
              y={rectY}
              width={rectW}
              height={rectH}
              rx={rxStroke}
              ry={rxStroke}
              fill="none"
              stroke={`url(#${gradId})`}
              strokeWidth={BORDER_WIDTH}
            />
          ) : (
            <AnimatedRect
              x={rectX}
              y={rectY}
              width={rectW}
              height={rectH}
              rx={rxStroke}
              ry={rxStroke}
              fill="none"
              stroke={`url(#${gradId})`}
              strokeWidth={BORDER_WIDTH}
              strokeLinecap="round"
              strokeDasharray={`${dashVisible},${dashGap}`}
              animatedProps={animatedStrokeProps}
            />
          )}
        </Svg>
      ) : null}
      <View
        className="bg-background"
        collapsable={false}
        style={[
          {
            borderRadius: INNER_BORDER_RADIUS,
            margin: BORDER_WIDTH,
            zIndex: 1,
          },
          VOICE_ASSESSMENT_CARD_INNER_PADDING_STYLE,
          // Slightly more vertical padding than base 12px: balances iOS text metrics + keeps top/bottom equal.
          { paddingTop: 14, paddingBottom: 14 },
        ]}
      >
        {children}
      </View>
    </View>
  )
}
